import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Loader2, Trash2, Check, RotateCcw, Phone } from 'lucide-react';
import { checkAuth, clearToken, adminLeadsList, markLead, deleteLead } from '../../admin/api';
import AdminTabs from './AdminTabs';
import BrandMark from '../../components/BrandMark';

const FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'new', label: '未处理' },
  { key: 'handled', label: '已处理' },
];

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function LeadsAdmin() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2400); };

  useEffect(() => {
    (async () => {
      const ok = await checkAuth();
      if (!ok) { nav('/admin/login'); return; }
      setReady(true);
    })();
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items, counts } = await adminLeadsList(filter);
      setItems(items); setCounts(counts);
    } catch (e) { flash(e.message); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const toggle = async (lead) => {
    try { await markLead(lead.id, lead.status === 'handled' ? 'new' : 'handled'); load(); }
    catch (e) { flash(e.message); }
  };
  const remove = async (lead) => {
    if (!window.confirm(`删除来自「${lead.name}」的留言？此操作不可恢复。`)) return;
    try { await deleteLead(lead.id); flash('已删除'); load(); } catch (e) { flash(e.message); }
  };

  const exportCsv = () => {
    const head = ['时间', '称呼', '电话', '身份', '应用场景', '来源', '状态'];
    const rows = items.map((l) => [fmt(l.createdAt), l.name, l.phone, l.role, l.childAge, l.source, l.status === 'handled' ? '已处理' : '未处理']);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `留言预约_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!ready) return <div className="flex min-h-screen items-center justify-center text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 加载中…</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandMark size="sm" />
            <div className="font-bold text-slate-900 max-sm:hidden">加太教育 · 管理后台</div>
          </div>
          <AdminTabs />
          <button onClick={() => { clearToken(); nav('/admin/login'); }} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer"><LogOut className="h-4 w-4" /> 退出</button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">留言 / 预约</h1>
            <p className="mt-1 text-sm text-gray-500">
              共 {counts.total} 条 · <span className="font-semibold text-blue-600">{counts.new}</span> 条未处理
            </p>
          </div>
          <button onClick={exportCsv} disabled={!items.length} className="btn btn-white py-2 text-sm disabled:opacity-50">导出 CSV</button>
        </div>

        <div className="mb-4 flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                filter === f.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-soft">
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 加载中…</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-gray-400">暂无留言</div>
          ) : (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">提交时间</th>
                  <th className="px-4 py-3 font-medium">称呼</th>
                  <th className="px-4 py-3 font-medium">电话</th>
                  <th className="px-4 py-3 font-medium">身份</th>
                  <th className="px-4 py-3 font-medium">应用场景</th>
                  <th className="px-4 py-3 font-medium">来源</th>
                  <th className="px-4 py-3 font-medium">状态</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className={`border-t border-gray-100 ${l.status === 'new' ? 'bg-blue-50/40' : ''}`}>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmt(l.createdAt)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{l.name}</td>
                    <td className="px-4 py-3">
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline"><Phone className="h-3.5 w-3.5" />{l.phone}</a>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{l.role}</td>
                    <td className="px-4 py-3 text-gray-600">{l.childAge || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{l.source}</td>
                    <td className="px-4 py-3">
                      {l.status === 'handled'
                        ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">已处理</span>
                        : <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-600">未处理</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggle(l)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 cursor-pointer" title={l.status === 'handled' ? '标记未处理' : '标记已处理'}>
                          {l.status === 'handled' ? <RotateCcw className="h-4 w-4" /> : <Check className="h-4 w-4 text-green-600" />}
                        </button>
                        <button onClick={() => remove(l)} className="rounded-lg p-2 text-red-500 hover:bg-red-50 cursor-pointer" title="删除"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">{toast}</div>}
    </div>
  );
}